import { useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useStableAuth } from './useStableAuth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';
import type { TaskActivity } from '@/types/task';
import { infiniteTaskKeys } from './useTasks';

const getActivityIcon = (activityType: string) => {
  switch (activityType) {
    case 'task_assigned': return '📋';
    case 'subtask_completed': return '✅';
    case 'followup_created': return '🔄';
    case 'parent_auto_closed': return '🎯';
    case 'task_unblocked': return '🔓';
    case 'promise_followup_created': return '🤝';
    case 'sale_task_completed': return '💰';
    case 'loan_task_completed': return '📦';
    case 'delivery_completed': return '🚚';
    case 'delivery_reopened': return '↩️';
    default: return '📌';
  }
};

const CACHE_INVALIDATION_TYPES = ['task_unblocked', 'promise_followup_created'];

export const useTaskActivities = () => {
  const { userId, companyId } = useStableAuth();
  const queryClient = useQueryClient();

  const { data: activities = [], isLoading: loading, refetch } = useQuery({
    queryKey: ['task-activities', userId, companyId],
    enabled: !!userId && !!companyId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('task_activities')
        .select(`
          *,
          task:tasks(id, title, status),
          actor:profiles!task_activities_actor_id_fkey(id, full_name, avatar_url)
        `)
        .eq('target_user_id', userId!)
        .eq('company_id', companyId!)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return (data || []) as unknown as TaskActivity[];
    },
    meta: {
      errorMessage: 'Erro ao carregar atividades'
    }
  });

  const unreadCount = activities.filter(a => !a.read).length;

  // Real-time subscription
  useEffect(() => {
    if (!userId || !companyId) return;

    const subscription = supabase
      .channel('task-activities-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'task_activities',
          filter: `target_user_id=eq.${userId}`
        },
        (payload) => {
          const newActivity = payload.new as TaskActivity;
          
          // Invalidate to refetch with joins
          queryClient.invalidateQueries({ queryKey: ['task-activities', userId, companyId] });

          const activityIcon = getActivityIcon(newActivity.activity_type);
          toast(
            `${activityIcon} ${newActivity.title}`,
            {
              description: newActivity.description,
              duration: 5000
            }
          );

          // Cross-cache invalidation: atualiza lista normal e Kanban (infinite)
          if (CACHE_INVALIDATION_TYPES.includes(newActivity.activity_type)) {
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            queryClient.invalidateQueries({ queryKey: infiniteTaskKeys.all });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'task_activities',
          filter: `target_user_id=eq.${userId}`
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['task-activities', userId, companyId] });
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [userId, companyId, queryClient]);

  const markAsRead = useCallback(async (activityId: string) => {
    try {
      const { error } = await supabase
        .from('task_activities')
        .update({ read: true, read_at: new Date().toISOString() })
        .eq('id', activityId);

      if (error) throw error;

      queryClient.setQueryData<TaskActivity[]>(
        ['task-activities', userId, companyId],
        (old) => old?.map(a => a.id === activityId ? { ...a, read: true, read_at: new Date().toISOString() } : a)
      );
    } catch (error) {
      logger.error('Erro ao marcar como lida:', error);
      toast.error('Erro ao marcar atividade como lida');
    }
  }, [userId, companyId, queryClient]);

  const markAllAsRead = useCallback(async () => {
    if (!userId || !companyId) return;

    try {
      const { error } = await supabase
        .from('task_activities')
        .update({ read: true, read_at: new Date().toISOString() })
        .eq('target_user_id', userId)
        .eq('company_id', companyId)
        .eq('read', false);

      if (error) throw error;

      queryClient.setQueryData<TaskActivity[]>(
        ['task-activities', userId, companyId],
        (old) => old?.map(a => ({ ...a, read: true, read_at: new Date().toISOString() }))
      );
      toast.success('Todas as atividades marcadas como lidas');
    } catch (error) {
      logger.error('Erro ao marcar todas como lidas:', error);
      toast.error('Erro ao atualizar atividades');
    }
  }, [userId, companyId, queryClient]);

  return {
    activities,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    refetch
  };
};
